/**
 * "Configure" dialog for what a grid page shows its reader.
 *
 * One place for both of the arrangements a user owns — the grid's columns and
 * the stat cards above it — because they are the same question asked twice and
 * splitting them across two controls made the reader hunt for the second one.
 * Each section lists its items in the order they appear, draggable, with a
 * checkbox for whether they appear at all.
 *
 * Nothing takes effect until Save, so a half-finished rearrangement never
 * reaches the page behind the dialog. Items the tenant withholds never reach
 * here at all — the backend leaves them out of the grid descriptor — so every
 * row is something the user is genuinely allowed to see. An item the grid locks
 * on shows a fixed checkbox but still drags, because being always-visible says
 * nothing about where it sits.
 *
 * Contains:
 * - GridConfigureDialog — the dialog (default export)
 */
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildDraftRows,
  draftToPreferences,
  isDraftDirty,
  moveDraftRow,
  toggleDraftVisibility,
} from '@sophocles/data-grids/grid/gridConfigDraft';

/** Pointer travel before a press becomes a drag, so the checkbox stays clickable. */
const DRAG_ACTIVATION_DISTANCE_PX = 5;

const COLUMNS_SECTION = 'columns';
const CARDS_SECTION = 'cards';

/**
 * One row: a drag handle, a shown/hidden checkbox, and the item's label. Only
 * the handle carries the drag listeners — putting them on the row would swallow
 * the checkbox's own clicks.
 *
 * A pinned row shows a pin in place of the handle and cannot be dragged. Its
 * position belongs to the pin rather than to the reader, so offering the handle
 * would promise a move the grid discards.
 *
 * @param {object} props
 * @param {{id: string, label: string, hideable: boolean, isMovable: boolean, isShown: boolean, description: (string|undefined)}} props.row
 * @param {(id: string) => void} props.onToggle
 * @param {string} props.dragLabel - accessible name for the handle
 * @param {string} props.lockedLabel - why a locked row cannot be unchecked
 * @param {string} props.pinnedLabel - why a pinned row cannot be moved
 * @returns {React.ReactElement}
 */
function SortableConfigRow({ row, onToggle, dragLabel, lockedLabel, pinnedLabel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !row.isMovable,
  });

  const label = (
    <Typography sx={{ flex: 1, minWidth: 0, color: row.isShown ? 'text.primary' : 'text.secondary' }} noWrap>
      {row.label}
    </Typography>
  );

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        // A dragged row rides above its neighbours and takes the surface
        // colour so it reads as lifted off the list rather than part of it.
        zIndex: isDragging ? 1 : 'auto',
        position: 'relative',
        backgroundColor: isDragging ? 'var(--md-surface-container)' : 'transparent',
        borderRadius: 'var(--card-radius)',
        paddingRight: 1,
      }}
    >
      {row.isMovable ? (
        <Box
          {...attributes}
          {...listeners}
          aria-label={dragLabel}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            color: 'text.secondary',
            padding: 0.5,
            touchAction: 'none',
            '&:focus-visible': {
              outline: '2px solid var(--md-primary)',
              outlineOffset: '2px',
              borderRadius: 'var(--card-radius)',
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      ) : (
        <Tooltip title={pinnedLabel}>
          <Box
            aria-label={pinnedLabel}
            sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', padding: 0.5 }}
          >
            <PushPinOutlinedIcon fontSize="small" />
          </Box>
        </Tooltip>
      )}

      <Checkbox
        size="small"
        checked={row.isShown}
        disabled={!row.hideable}
        onChange={() => onToggle(row.id)}
        inputProps={{ 'aria-label': row.label }}
        title={row.hideable ? undefined : lockedLabel}
      />

      {row.description ? (
        <Tooltip title={row.description} placement="top">
          {label}
        </Tooltip>
      ) : (
        label
      )}
    </Stack>
  );
}

/**
 * A draggable, checkable list for one section of the dialog.
 *
 * @param {object} props
 * @param {Array<object>} props.rows
 * @param {(rows: Array<object>) => void} props.onRowsChange
 * @returns {React.ReactElement}
 */
function ConfigSection({ rows, onRowsChange }) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Pinned rows stay out of the sortable set entirely, so a drag can neither
  // pick one up nor land on one.
  const sortableIds = useMemo(() => rows.filter(row => row.isMovable).map(row => row.id), [rows]);

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    onRowsChange(moveDraftRow(rows, active.id, over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <Stack spacing={0.25}>
          {rows.map(row => (
            <SortableConfigRow
              key={row.id}
              row={row}
              onToggle={id => onRowsChange(toggleDraftVisibility(rows, id))}
              dragLabel={t('gridConfigure.reorderHandle', 'Reorder')}
              lockedLabel={t('gridConfigure.alwaysShown', 'Always shown')}
              pinnedLabel={t('gridConfigure.pinnedColumn', 'Pinned — position is fixed')}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {?{items: Array<object>, visibilityModel: object}} props.columns -
 *   the grid's configurable columns, or null when this grid cannot offer them
 * @param {?{items: Array<object>, visibilityModel: object}} props.statCards -
 *   the descriptor's stat cards, or null when this grid has none
 * @param {(result: object) => void} props.onSave - receives `{ columns, statCards }`,
 *   each `{ order, visibilityModel }` and present only for a section that changed
 * @returns {React.ReactElement}
 */
export default function GridConfigureDialog({ open, onClose, columns, statCards, onSave }) {
  const { t } = useTranslation();
  // Seeded once, at mount. The caller mounts this dialog only while it is open,
  // so a fresh open always starts from what is saved — and the draft survives
  // the parent's re-renders, which rebuild the item lists on every pass and
  // would otherwise reset a rearrangement halfway through it.
  const [columnRows, setColumnRows] = useState(() =>
    columns ? buildDraftRows(columns.items, columns.visibilityModel) : [],
  );
  const [cardRows, setCardRows] = useState(() =>
    statCards ? buildDraftRows(statCards.items, statCards.visibilityModel) : [],
  );

  const hasColumns = Boolean(columns);
  const hasCards = Boolean(statCards);
  const [section, setSection] = useState(hasColumns ? COLUMNS_SECTION : CARDS_SECTION);

  const columnsDirty = hasColumns && isDraftDirty(columnRows, columns.items, columns.visibilityModel);
  const cardsDirty = hasCards && isDraftDirty(cardRows, statCards.items, statCards.visibilityModel);
  const isDirty = columnsDirty || cardsDirty;

  const handleSave = () => {
    onSave({
      columns: columnsDirty ? draftToPreferences(columnRows) : undefined,
      statCards: cardsDirty ? draftToPreferences(cardRows) : undefined,
    });
    onClose();
  };

  const showTabs = hasColumns && hasCards;
  const activeSection = showTabs ? section : hasColumns ? COLUMNS_SECTION : CARDS_SECTION;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { height: 'min(34rem, 90vh)' } } }}
    >
      <DialogTitle sx={{ paddingBottom: 1 }}>{t('gridConfigure.title', 'Configure')}</DialogTitle>
      {showTabs && (
        <Tabs
          value={section}
          onChange={(_, next) => setSection(next)}
          variant="fullWidth"
          sx={{ paddingX: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value={COLUMNS_SECTION} label={t('gridConfigure.gridColumns', 'Grid Columns')} />
          <Tab value={CARDS_SECTION} label={t('gridConfigure.statCards', 'Stat Cards')} />
        </Tabs>
      )}
      <DialogContent dividers={!showTabs}>
        <DialogContentText sx={{ fontSize: '0.875rem', marginBottom: 1.5 }}>
          {activeSection === COLUMNS_SECTION
            ? t('gridConfigure.helpColumns', 'Drag to reorder. Uncheck a column to hide it from the grid.')
            : t('gridConfigure.helpCards', 'Drag to reorder. Uncheck a card to hide it from this page.')}
        </DialogContentText>

        {activeSection === COLUMNS_SECTION ? (
          <ConfigSection rows={columnRows} onRowsChange={setColumnRows} />
        ) : (
          <ConfigSection rows={cardRows} onRowsChange={setCardRows} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
        <Button variant="contained" disabled={!isDirty} onClick={handleSave}>
          {t('actions.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
